/**
 * WHAT A TECHNICIAN EARNS ON A JOB.
 *
 * Anthony: "mobile work paid at two thirds of labor plus half the parts
 * markup."
 *
 * That is the whole rule and it has never been written down anywhere the
 * software could read it. `pay_commissions` was built with exactly the
 * right columns -- job_id, labour_amount, parts_markup, amount -- and then
 * every row in it was typed in by hand or carried over from the old
 * system. Nothing computed anything, so a job with $322 of parts markup on
 * it recorded $0 of parts and paid the technician $161 less than the rule
 * says they earned.
 *
 * ─── THE TWO HALVES ────────────────────────────────────────────────────
 *
 * LABOR is the easy half: the labor lines on the job, times their share.
 *
 * PARTS is the half that was being lost. The technician is not paid a
 * share of what the part SOLD for -- that would pay them for the shop's
 * own money tied up in the part. They are paid a share of the MARKUP: what
 * the customer paid minus what the shop paid. On a $400 part bought for
 * $260 the markup is $140 and half of it is $70.
 *
 * A LINE WITH NO COST RECORDED CONTRIBUTES NOTHING. It is tempting to
 * treat a missing cost as zero and call the whole sale markup, and that
 * would hand somebody 50% of a part the shop paid full price for. Unknown
 * is not free. Those lines are counted and reported rather than guessed
 * at, so the gap is visible instead of expensive.
 *
 * A LINE CANNOT GO NEGATIVE. A part sold at or under cost -- a goodwill
 * discount, a price matched to a competitor -- is a decision the shop
 * made, and it must not claw money back out of the technician's half of
 * some other part on the same invoice.
 *
 * ─── AND IT IS SPLIT, NOT DUPLICATED ───────────────────────────────────
 *
 * Two technicians on one job share the job, they do not each earn it. The
 * split is even, because nothing in the system records who did which half
 * and an invented weighting is worse than an honest equal one.
 */

/** What the job is worth before anybody's percentage is applied. */
export type JobValue = {
  /** Every labor line on the job, summed. */
  labour: number;
  /** Sale minus cost on every part line that HAS a cost, never negative. */
  partsMarkup: number;
  /** Part lines with no cost on file. Counted so the gap can be said out loud. */
  partsWithNoCost: number;
};

/**
 * One person's terms. Missing percentages mean they earn nothing here.
 *
 * WHERE THE WORK HAPPENED CHANGES THE RATE. Anthony, 2026-10-03: shop
 * technicians make 50% of labor and 50% of the parts markup, "so does a
 * mobile tech if they do a shop job though." So the mobile rate is the
 * person's own and the shop rate is a second pair beside it. A shop pair
 * left empty falls back to the mobile one, which is what the two
 * franchise-style techs at 80% want.
 */
export type CommissionTerms = {
  commission_pct: number | null;
  parts_pct: number | null;
  shop_commission_pct?: number | null;
  shop_parts_pct?: number | null;
};

/**
 * The pair that applies to one job. A job with no service_at recorded is
 * read as mobile, because 1,837 of the shop's 2,188 jobs are mobile and
 * the rest of the blanks are migrated history.
 */
export function termsFor(t: CommissionTerms, serviceAt: string | null | undefined): CommissionTerms {
  if (String(serviceAt ?? "").toLowerCase() !== "shop") return t;
  return {
    commission_pct: t.shop_commission_pct ?? t.commission_pct,
    parts_pct: t.shop_parts_pct ?? t.parts_pct,
  };
}

export type Share = {
  /** This person's slice of the job's labor, BEFORE their percentage. */
  labourAmount: number;
  /** This person's slice of the job's parts markup, BEFORE their percentage. */
  partsMarkup: number;
  /** What they actually earned: the two slices with their percentages on. */
  amount: number;
};

type Line = {
  kind: string | null;
  total: number | null;
  cost: number | null;
  quantity: number | null;
};

const num = (v: unknown): number => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/** Money, to the cent, so a stored total never carries a floating tail. */
export const cents = (n: number): number => Math.round(n * 100) / 100;

/**
 * Add a job's lines up into the two numbers the rule needs.
 *
 * `total` is a generated column on line_items -- quantity times unit price
 * -- but `cost` is per unit, so the cost side has to be multiplied out. A
 * quantity of four brake pads at $30 cost is $120 of cost against the
 * line, not $30.
 */
export function valueOf(lines: Line[]): JobValue {
  let labour = 0, partsMarkup = 0, partsWithNoCost = 0;

  for (const l of lines) {
    const kind = String(l.kind ?? "").toLowerCase();
    if (kind === "labour" || kind === "labor") {
      labour += num(l.total);
      continue;
    }
    if (kind !== "part") continue;

    // Unknown is not free. A part with no cost on file is not markup.
    if (l.cost === null || l.cost === undefined) { partsWithNoCost += 1; continue; }

    const qty = l.quantity === null || l.quantity === undefined ? 1 : num(l.quantity);
    partsMarkup += Math.max(num(l.total) - num(l.cost) * qty, 0);
  }

  return { labour: cents(labour), partsMarkup: cents(partsMarkup), partsWithNoCost };
}

/**
 * One technician's share of one job.
 *
 * `hands` is how many technicians are on the job. Zero or one both mean
 * the whole job, because a job with nobody on it should never reach here
 * and dividing by zero would be a silent infinity in somebody's pay.
 */
export function shareOf(
  value: JobValue, terms: CommissionTerms, hands: number,
): Share {
  const split = hands > 1 ? hands : 1;
  const labourAmount = cents(value.labour / split);
  const partsMarkup = cents(value.partsMarkup / split);

  // No terms on file earns nothing rather than something invented. A
  // person being paid on a rule nobody wrote down is how the last system
  // ended up with numbers no one could reproduce.
  const labourPct = num(terms.commission_pct);
  const partsPct = num(terms.parts_pct);

  return {
    labourAmount,
    partsMarkup,
    amount: cents(labourAmount * labourPct + partsMarkup * partsPct),
  };
}

/**
 * The shop's standard deal, in the words Anthony used for it. Two thirds
 * exactly, not 67%: on $225 of labor 67% pays $150.75 and he pays $150.
 */
export const STANDARD_TERMS: CommissionTerms = {
  commission_pct: 0.66667,
  parts_pct: 0.5,
  shop_commission_pct: 0.5,
  shop_parts_pct: 0.5,
};

/** Said in one line on a screen, so nobody has to reconstruct the sum. */
export function explain(s: Share, terms: CommissionTerms): string {
  const pct = (n: number | null | undefined) => pctLabel(num(n));
  const bits: string[] = [];
  if (s.labourAmount) {
    bits.push(`${pct(terms.commission_pct)} of $${s.labourAmount.toFixed(2)} labor`);
  }
  if (s.partsMarkup) {
    bits.push(`${pct(terms.parts_pct)} of $${s.partsMarkup.toFixed(2)} parts markup`);
  }
  return bits.length ? bits.join(" plus ") : "nothing chargeable on this job";
}

/** "2/3" for two thirds, otherwise a whole percent. */
export function pctLabel(n: number): string {
  if (Math.abs(n - 2 / 3) < 0.001) return "2/3";
  if (Math.abs(n - 1 / 3) < 0.001) return "1/3";
  return `${Math.round(n * 100)}%`;
}
